import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {DcpApiService, DcpGlobalMessageService} from '@dcp/configurator';
import * as jwkToPem from 'jwk-to-pem';
import * as jwt from 'jsonwebtoken';
import {filter, first, map, switchMap, take} from 'rxjs/operators';
import {PaymentProviderService} from '@app/payment/services/payment-provider.service';
import {ActiveCartFacade, Cart} from '@spartacus/cart/base/root';
import {CyberSourcePaymentState, PaymentState} from '@app/payment/services/store/payment-state';
import {PlaceOrderRequestBody} from '@app/model/order/dcp-place-order-body.model';
import {PayerAuthSetupResponse} from '@app/model/cyber-source/payer-auth-setup-response';
import {CaptureContext} from '@app/model/cyber-source/capture-context';
import {PayerAuthEnrollmentResponse} from '@app/model/cyber-source/payer-auth-enrollment-response';
import * as cyberSourceActions from '@app/payment/services/store/actions/cyber-source.action';
import {Store} from '@ngrx/store';
import * as fromCyberSource from '@app/payment/services/store/reducers/cyber-source.reducer';
import {CyberSourcePaymentErrorService} from '@app/payment/services/cybersource/error-handling/cyber-source-payment-error.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class CyberSourceService extends PaymentProviderService {

  private isOpen$ = new BehaviorSubject<boolean>(false);
  private refresh$ = new Subject<boolean>();
  private refreshTimerRef;

  constructor(
    private apiService: DcpApiService,
    private activeCartService: ActiveCartFacade,
    private store: Store,
    private dcpGlobalMessageService: DcpGlobalMessageService,
    private cyberSourcePaymentErrorService: CyberSourcePaymentErrorService,
  ) {
    super();
  }

  getCaptureContext(vendorId: string, salesOrgCode: string, currency: string): Observable<CaptureContext> {
    return this.apiService.callKyma('cyberSourceIntegration', 'captureContext', {}, {
      body: {
        vendorId,
        salesOrgCode,
        currency
      }
    }, 'POST');
  }

  getPayerAuthEnrollment(jti: string, referenceId: string = undefined, authTransactionId = undefined): Observable<PayerAuthEnrollmentResponse> {
    return this.activeCartService.getActive().pipe(take(1), switchMap((cart: Cart) =>
      this.apiService.callKyma<PayerAuthEnrollmentResponse>('cyberSourceIntegration', 'payerAuthEnrollment', {}, {
        body: {
          cartId: cart.code,
          tokenInformation: {
            transientTokenJwt: jti,
            ...(referenceId && {referenceId}),
            ...(authTransactionId && {authTransactionId}),
          },
          saveCard: true,
        }
      }, 'POST')
    ));
  }

  getPayerAuthEnrollmentOnPlaceOrder(referenceId: string, authTransactionId = undefined): Observable<PayerAuthEnrollmentResponse> {
    return this.activeCartService.getActive().pipe(take(1), switchMap((cart: Cart) =>
      this.apiService.callKyma<PayerAuthEnrollmentResponse>('cyberSourceIntegration', 'payerAuthEnrollment', {}, {
        body: {
          cartId: cart.code,
          tokenInformation: {
            referenceId,
            ...(authTransactionId && {authTransactionId}),
          }
        }
      }, 'POST')
    ));
  }

  getPayerAuthSetup(expiryMonth: string, expiryYear: string, transientToken: string): Observable<PayerAuthSetupResponse> {
    return this.activeCartService.getActive().pipe(take(1), switchMap((cart: Cart) =>
      this.apiService.callKyma<PayerAuthSetupResponse>('cyberSourceIntegration', 'payerAuthSetup', {}, {
        body: {
          saveCard: true,
          cartId: cart.code,
          cardDetails: {
            expiryMonth,
            expiryYear,
          },
          tokenInformation: {
            transientToken
          }
        }
      }, 'POST')
    ));
  }

  getPayerAuthSetupOnPlaceOrder(): Observable<PayerAuthSetupResponse> {
    return this.activeCartService.getActive().pipe(take(1), switchMap((cart: Cart) => {
      return this.apiService.callKyma<PayerAuthSetupResponse>('cyberSourceIntegration', 'payerAuthSetup', {}, {
        body: {
          cartId: cart.code
        }
      }, 'POST');
    }));
  }

  validateCaptureContext(captureContext: string): Observable<boolean> {
    if (typeof captureContext !== 'string') {
      throw new TypeError(`Expected a string for 'captureContext', but got ${typeof captureContext}`);
    }
    const [headerCaptureContext] = captureContext.split('.');

    // Decode the Base64-encoded header
    const header = JSON.parse(atob(headerCaptureContext));

    const kid = header.kid;

    return this.apiService.callKyma('cyberSourceIntegration', 'publicKey', {kid}).pipe(
      map((publicKey: string) => {
        try {
          const pem = this.convertJwkToPem(publicKey);
          // Verify the JWT signature using the public key
          jwt.verify(captureContext, pem, {algorithms: ['RS256']});
          return true;
        } catch (error) {
          return false;
        }
      })
    );
  }

  shouldRefreshForm$(): Observable<boolean> {
    return this.refresh$.asObservable();
  }

  stopRefreshTimer(): void {
    clearTimeout(this.refreshTimerRef);
    this.refreshTimerRef = null;
  }

  startRefreshTimer(): void {
    this.refreshTimerRef = setTimeout(() => {
      this.refresh$.next(true);
    }, 1000 * 60 * 15); // captureContext token is valid for 15 minutes, then needs to be refreshed
  }

  convertJwkToPem(publicKey: string): any {
    return jwkToPem(publicKey);
  }

  openAddNewCreditCardComponent(currency: string = undefined): void {
    this.isOpen$.next(true);
  }

  closeAddNewCreditCardComponent(): void {
    this.isOpen$.next(false);
  }

  setPspMerchantId(cart: Cart): void {
    return;
  }

  isAddNewCreditCardComponentOpened(): Observable<boolean> {
    return this.isOpen$;
  }

  convertStateToPlaceOrderPayload(): Observable<PlaceOrderRequestBody> {
    return this.getPaymentState().pipe(
      filter((state) => this.validatePaymentState(state)),
      first(),
      map(state => state.data
      )
    );
  }

  startCcFlow(): void {
    this.store.dispatch(new cyberSourceActions.StartPaymentFlow());
  }

  getPaymentState(): Observable<PaymentState> {
    return this.store.select(fromCyberSource.getState);
  }

  validatePaymentState(state: CyberSourcePaymentState): boolean {
    if (state.error) {
      throw state;
    }
    return !!state.data;
  }

  displayPaymentError(state: CyberSourcePaymentState): void {
    this.cyberSourcePaymentErrorService.getMessage(state).pipe(
      first(),
      untilDestroyed(this),
    ).subscribe(message => {
      this.dcpGlobalMessageService.addMessage(message, this.constructor.name, 'errorGlobal');
    });
  }

  saveAuthorizationErrors(): void {
    return;
  }
}
